// _components/onboarding/consultation/ConsultStep3.tsx

import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useState } from "react";

type Step3Props = {
  onBack: () => void;
  onNext: (data: { budget: string }) => void;
  progressBar?: React.ReactNode;
};

export default function ConsultStep3({ onBack, onNext, progressBar }: Step3Props) {
  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);

  const budgets = [
    { id: "100k-500k", label: "₦100,000 – ₦500,000" },
    { id: "500k-1m", label: "₦500,000 – ₦1M" },
    { id: "1m-5m", label: "₦1M – ₦5M" },
    { id: "5m-20m", label: "₦5M – ₦20M" },
    { id: "not-sure", label: "Not sure" },
  ];

  const handleSelect = (id: string) => {
    setSelectedBudget(id);
  };

  const handleContinue = () => {
    if (!selectedBudget) return;
    onNext({ budget: selectedBudget });
  };

  return (
    <div className="text-center">
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-semibold text-gray-800"
      >
        What's your budget?
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-2 text-sm text-gray-600"
      >
        Select your budget range in Naira to help us find affordable solar
        options that fit your needs.
      </motion.p>

      {progressBar}

      <div className="mt-8 space-y-4">
        {budgets.map((item) => (
          <motion.button
            key={item.id}
            whileTap={{ scale: 0.98 }}
            whileHover={{ scale: 1.01 }}
            onClick={() => handleSelect(item.id)}
            className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${selectedBudget === item.id
                ? "border-green-700 bg-white shadow-md"
                : "border-gray-200 hover:border-gray-300"
              }`}
          >
            <span className="text-lg font-medium text-gray-800">
              {item.label}
            </span>
            <div
              className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${selectedBudget === item.id
                  ? "border-green-600"
                  : "border-gray-300"
                }`}
            >
              {selectedBudget === item.id && (
                <div className="h-3 w-3 rounded-full bg-green-600"></div>
              )}
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-10 flex justify-end gap-6">
        <Button
          size={"lg"}
          variant={"outline"}
          onClick={onBack}
          className="rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
        >
          ← Back
        </Button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          disabled={!selectedBudget}
          onClick={handleContinue}
          className={`px-6 py-3 rounded-full font-medium ${selectedBudget
              ? "bg-green-800 text-white hover:bg-green-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
        >
          Continue
        </motion.button>
      </div>
    </div>
  );
}
